import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Heart, Lock, TrendingUp, Award, Target } from 'lucide-react';
import { toast } from 'sonner';

interface AttachmentAssessment {
  id: string;
  timestamp: string;
  relationshipId: string;
  anxiousScore: number;
  avoidantScore: number;
  style: 'anxious' | 'avoidant' | 'secure' | 'fearful-avoidant';
}

interface AttachmentJourneyProps {
  relationshipId: string;
  tier: 'free' | 'premium' | 'pro';
}

const ANXIOUS_QUESTIONS = [
  'I worry about being abandoned or left',
  'I need frequent reassurance that they care about me',
  'I get anxious when they don\'t respond quickly',
  'I often feel they don\'t want to be as close as I do',
  'I worry they\'ll lose interest in me',
];

const AVOIDANT_QUESTIONS = [
  'I prefer not to depend on others emotionally',
  'I feel uncomfortable when people get too close',
  'I value independence over emotional intimacy',
  'I tend to pull away when things get too intense',
  'I rarely share my deepest feelings',
];

export function AttachmentJourney({ relationshipId, tier }: AttachmentJourneyProps) {
  const [showAssessment, setShowAssessment] = useState(false);
  const [anxiousAnswers, setAnxiousAnswers] = useState<number[]>(new Array(5).fill(0));
  const [avoidantAnswers, setAvoidantAnswers] = useState<number[]>(new Array(5).fill(0));
  const [assessments, setAssessments] = useState<AttachmentAssessment[]>([]);

  const isPro = tier === 'pro';

  useEffect(() => {
    loadAssessments();
  }, [relationshipId]);

  const loadAssessments = () => {
    const stored = localStorage.getItem('tension_attachment_assessments');
    if (stored) {
      const all = JSON.parse(stored);
      const filtered = all
        .filter((a: AttachmentAssessment) => a.relationshipId === relationshipId)
        .sort((a: AttachmentAssessment, b: AttachmentAssessment) => 
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
      setAssessments(filtered);
    }
  };

  const calculateStyle = (anxiousScore: number, avoidantScore: number): 'anxious' | 'avoidant' | 'secure' | 'fearful-avoidant' => {
    const anxiousAvg = anxiousScore / 5;
    const avoidantAvg = avoidantScore / 5;

    if (anxiousAvg >= 4 && avoidantAvg >= 4) {
      return 'fearful-avoidant';
    } else if (anxiousAvg >= 4) {
      return 'anxious';
    } else if (avoidantAvg >= 4) {
      return 'avoidant';
    } else {
      return 'secure';
    }
  };

  const handleCompleteAssessment = () => {
    const anxiousScore = anxiousAnswers.reduce((sum, val) => sum + val, 0);
    const avoidantScore = avoidantAnswers.reduce((sum, val) => sum + val, 0);
    const style = calculateStyle(anxiousScore, avoidantScore);

    const assessment: AttachmentAssessment = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      relationshipId,
      anxiousScore,
      avoidantScore,
      style,
    };

    const stored = localStorage.getItem('tension_attachment_assessments') || '[]';
    const all = JSON.parse(stored);
    all.push(assessment);
    localStorage.setItem('tension_attachment_assessments', JSON.stringify(all));

    toast.success('Assessment complete! View your results below.');
    setShowAssessment(false);
    setAnxiousAnswers(new Array(5).fill(0));
    setAvoidantAnswers(new Array(5).fill(0));
    loadAssessments();
  };

  const getStyleInfo = (style: string) => {
    switch (style) {
      case 'anxious':
        return {
          name: 'Anxious Attachment',
          color: 'text-orange-600',
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          description: 'You crave closeness but fear abandonment. High pursuit, frequent reassurance-seeking.',
          strengths: ['Emotionally expressive', 'Values intimacy', 'Communicative about needs'],
          challenges: ['Fear of abandonment', 'Overanalyzes behavior', 'Needs frequent validation'],
          growth: [
            'Practice self-soothing instead of seeking external validation',
            'Build independence and self-worth outside the relationship',
            'Challenge catastrophic thinking patterns',
          ],
        };
      case 'avoidant':
        return {
          name: 'Avoidant Attachment',
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          description: 'You value independence and feel uncomfortable with emotional closeness.',
          strengths: ['Self-reliant', 'Values autonomy', 'Calm under pressure'],
          challenges: ['Difficulty with emotional intimacy', 'Tendency to withdraw', 'Suppresses emotions'],
          growth: [
            'Practice vulnerability in small doses',
            'Recognize withdrawal patterns and pause before pulling away',
            'Communicate needs for space rather than disappearing',
          ],
        };
      case 'fearful-avoidant':
        return {
          name: 'Fearful-Avoidant (Disorganized)',
          color: 'text-purple-600',
          bgColor: 'bg-purple-50',
          borderColor: 'border-purple-200',
          description: 'You crave closeness but fear it. Push-pull dynamic between anxious and avoidant.',
          strengths: ['Deeply introspective', 'Aware of patterns', 'Capable of transformation'],
          challenges: ['Mixed signals', 'Approach-avoidance conflict', 'Intense emotional swings'],
          growth: [
            'Work with a therapist to process early attachment wounds',
            'Practice consistent behavior instead of push-pull',
            'Build emotional regulation skills',
          ],
        };
      case 'secure':
        return {
          name: 'Secure Attachment',
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          description: 'You balance intimacy and independence. Comfortable with closeness and autonomy.',
          strengths: ['Emotionally regulated', 'Trusting', 'Good boundaries', 'Effective communication'],
          challenges: ['Situational anxiety can emerge', 'May underestimate partner\'s insecurity'],
          growth: [
            'Continue modeling secure behavior',
            'Help partner feel safe through consistency',
            'Maintain self-awareness during stress',
          ],
        };
      default:
        return null;
    }
  };

  const getJourneyProgress = () => {
    if (assessments.length < 2) return null;

    const latest = assessments[0];
    const first = assessments[assessments.length - 1];

    const anxiousImprovement = first.anxiousScore - latest.anxiousScore;
    const avoidantImprovement = first.avoidantScore - latest.avoidantScore;

    const daysBetween = Math.floor(
      (new Date(latest.timestamp).getTime() - new Date(first.timestamp).getTime()) / (1000 * 60 * 60 * 24)
    );

    return {
      anxiousChange: anxiousImprovement,
      avoidantChange: avoidantImprovement,
      daysBetween,
      styleChange: first.style !== latest.style,
      previousStyle: first.style,
      currentStyle: latest.style,
    };
  };

  const progress = getJourneyProgress();
  const latestAssessment = assessments[0];
  const styleInfo = latestAssessment ? getStyleInfo(latestAssessment.style) : null;

  if (!isPro) {
    return (
      <Card className="p-6 bg-gradient-to-br from-pink-50 to-rose-50 border-pink-200">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-pink-100 rounded-lg">
            <Lock className="w-5 h-5 text-pink-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">PRO</span>
              <h3 className="font-medium text-neutral-900">Attachment Style Journey</h3>
            </div>
            <p className="text-sm text-neutral-600 mb-3">
              Track your attachment style over time. See your transformation from anxious/avoidant → secure.
            </p>
            <div className="bg-white/60 rounded-lg p-3 mb-3">
              <p className="text-xs text-neutral-700 mb-2">
                <strong>Track your evolution:</strong>
              </p>
              <div className="space-y-1 text-xs text-neutral-600">
                <p>• Monthly attachment assessments (10 questions, 2 minutes)</p>
                <p>• See your anxious/avoidant scores change over time</p>
                <p>• Personalized growth exercises based on your style</p>
                <p>• Celebrate milestones: anxious → secure progression</p>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => window.location.href = '/settings'}
              className="gap-2"
            >
              Upgrade to Pro
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="p-6 bg-gradient-to-br from-pink-50 to-rose-50 border-pink-200">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-600" />
              <h3 className="font-medium text-neutral-900">Attachment Style Journey</h3>
              <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">PRO</span>
            </div>
            {!showAssessment && (
              <Button
                size="sm"
                onClick={() => setShowAssessment(true)}
                variant="outline"
              >
                New Assessment
              </Button>
            )}
          </div>

          <p className="text-sm text-neutral-600">
            {assessments.length === 0 
              ? 'Take your first attachment style assessment to begin tracking your journey toward secure attachment.'
              : 'Track your attachment evolution over time with monthly assessments.'}
          </p>

          {/* Assessment Form */}
          {showAssessment && (
            <div className="bg-white rounded-lg p-4 border border-pink-200 space-y-4">
              <div>
                <h4 className="font-medium text-neutral-900 mb-3">Anxious Attachment Indicators</h4>
                <p className="text-xs text-neutral-600 mb-3">Rate each statement: 1 (never) to 7 (always)</p>
                {ANXIOUS_QUESTIONS.map((question, idx) => (
                  <div key={idx} className="mb-4">
                    <p className="text-sm text-neutral-700 mb-2">{question}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-neutral-500">Never</span>
                      <input
                        type="range"
                        min="1"
                        max="7"
                        value={anxiousAnswers[idx]}
                        onChange={(e) => {
                          const updated = [...anxiousAnswers];
                          updated[idx] = parseInt(e.target.value);
                          setAnxiousAnswers(updated);
                        }}
                        className="flex-1"
                      />
                      <span className="text-xs text-neutral-500">Always</span>
                      <span className="text-sm font-medium text-neutral-900 w-6">{anxiousAnswers[idx]}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-neutral-200 pt-4">
                <h4 className="font-medium text-neutral-900 mb-3">Avoidant Attachment Indicators</h4>
                <p className="text-xs text-neutral-600 mb-3">Rate each statement: 1 (never) to 7 (always)</p>
                {AVOIDANT_QUESTIONS.map((question, idx) => (
                  <div key={idx} className="mb-4">
                    <p className="text-sm text-neutral-700 mb-2">{question}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-neutral-500">Never</span>
                      <input
                        type="range"
                        min="1"
                        max="7"
                        value={avoidantAnswers[idx]}
                        onChange={(e) => {
                          const updated = [...avoidantAnswers];
                          updated[idx] = parseInt(e.target.value);
                          setAvoidantAnswers(updated);
                        }}
                        className="flex-1"
                      />
                      <span className="text-xs text-neutral-500">Always</span>
                      <span className="text-sm font-medium text-neutral-900 w-6">{avoidantAnswers[idx]}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <Button onClick={handleCompleteAssessment} className="flex-1">
                  Complete Assessment
                </Button>
                <Button
                  onClick={() => setShowAssessment(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Latest Results */}
          {latestAssessment && styleInfo && !showAssessment && (
            <div className={`${styleInfo.bgColor} rounded-lg p-4 border ${styleInfo.borderColor}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className={`font-medium ${styleInfo.color}`}>{styleInfo.name}</h4>
                  <p className="text-xs text-neutral-500">
                    Last assessed: {new Date(latestAssessment.timestamp).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-neutral-600">Anxious: {latestAssessment.anxiousScore}/35</p>
                  <p className="text-xs text-neutral-600">Avoidant: {latestAssessment.avoidantScore}/35</p>
                </div>
              </div>
              <p className="text-sm text-neutral-700 mb-3">{styleInfo.description}</p>

              <div className="space-y-2">
                <div>
                  <p className="text-xs font-medium text-neutral-900 mb-1">Your Strengths:</p>
                  <ul className="text-xs text-neutral-700 space-y-0.5">
                    {styleInfo.strengths.map((s, idx) => (
                      <li key={idx}>• {s}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-medium text-neutral-900 mb-1">Growth Opportunities:</p>
                  <ul className="text-xs text-neutral-700 space-y-0.5">
                    {styleInfo.growth.map((g, idx) => (
                      <li key={idx}>→ {g}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Journey Progress */}
          {progress && (
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-5 h-5 text-purple-600" />
                <h4 className="font-medium text-neutral-900">Your Journey ({progress.daysBetween} days)</h4>
              </div>
              
              {progress.styleChange && (
                <div className="bg-white/60 rounded-lg p-3 mb-3 flex items-center gap-3">
                  <Award className="w-6 h-6 text-purple-600" />
                  <div>
                    <p className="text-sm font-medium text-purple-900">Attachment Style Shift!</p>
                    <p className="text-xs text-purple-700">
                      {progress.previousStyle} → {progress.currentStyle}
                    </p>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-neutral-700">Anxious behaviors</span>
                  <span className={`text-sm font-medium ${
                    progress.anxiousChange > 0 ? 'text-green-600' : 'text-neutral-600'
                  }`}>
                    {progress.anxiousChange > 0 ? '↓' : progress.anxiousChange < 0 ? '↑' : '→'} {Math.abs(progress.anxiousChange)} points
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-neutral-700">Avoidant behaviors</span>
                  <span className={`text-sm font-medium ${
                    progress.avoidantChange > 0 ? 'text-green-600' : 'text-neutral-600'
                  }`}>
                    {progress.avoidantChange > 0 ? '↓' : progress.avoidantChange < 0 ? '↑' : '→'} {Math.abs(progress.avoidantChange)} points
                  </span>
                </div>
              </div>

              {(progress.anxiousChange > 5 || progress.avoidantChange > 5) && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-3">
                  <p className="text-sm text-green-900 font-medium">
                    🎉 Significant progress! You're moving toward secure attachment.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Assessment History */}
      {assessments.length > 1 && (
        <Card className="p-6">
          <h4 className="font-medium text-neutral-900 mb-3">Assessment History</h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {assessments.map((assessment) => {
              const info = getStyleInfo(assessment.style);
              return (
                <div
                  key={assessment.id}
                  className={`${info?.bgColor} rounded-lg p-3 border ${info?.borderColor}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm font-medium ${info?.color}`}>{info?.name}</p>
                      <p className="text-xs text-neutral-500">
                        {new Date(assessment.timestamp).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right text-xs text-neutral-600">
                      <p>Anxious: {assessment.anxiousScore}/35</p>
                      <p>Avoidant: {assessment.avoidantScore}/35</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}
